import { withVdMartBuyer, isVdMartLocation } from '../../utils/vdMartPOS';
// 📁 src/features/orders/orderSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { updateProductStockOnly, applyOrderStock } from '../products/productSlice';
import { enqueueOrder, peekOrdersQueue, setOrdersQueue } from '../../utils/offlineStorage';
import { fetchCustomerByPhone, createCustomer } from '../customers/customerSlice'; 
import { API_BASE_URL, getNetworkFailureMessage } from '../../utils/apiConfig';
import { normalizeOrderDiscountPayload } from '../../utils/orderDiscount';
const generateMKOrderId = () => Number(`${Date.now()}${Math.floor(Math.random() * 90 + 10)}`);

const getStoredToken = () => {
  try {
    const saved = localStorage.getItem('posUserInfo');
    return saved ? JSON.parse(saved)?.token : '';
  } catch {
    return '';
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.posUsers)) return payload.posUsers;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const isDeliveryRole = (user) =>
  String(user?.role || user?.userRole || user?.type || '')
    .toLowerCase()
    .includes('delivery');

export const fetchDeliveryPosUsers = createAsyncThunk(
  'orders/fetchDeliveryPosUsers',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().posUser?.userInfo?.token || getStoredToken();

    const urls = [
      `${API_BASE_URL}/posusers?role=delivery`,
      `${API_BASE_URL}/posusers`,
      `${API_BASE_URL}/pos_users?role=delivery`,
      `${API_BASE_URL}/pos_users`,
    ];

    let lastError = 'Failed to fetch delivery users';

    for (const url of urls) {
      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          lastError = data.message || lastError;
          continue;
        }

        const users = normalizeArray(data);
        return users.filter(isDeliveryRole);
      } catch (error) {
        lastError = error.message || lastError;
      }
    }

    return thunkAPI.rejectWithValue(lastError);
  }
);
// -----------------------------
// Save order offline (no network)
// -----------------------------
export const queueOrder = createAsyncThunk(
  'orders/queueOrder',
  async ({ payload, token, cartItems, phone, customerMeta }, thunkAPI) => {
    const vdMart = isVdMartLocation(thunkAPI.getState().posUser.userInfo?.location);
    const payLater = payload.paymentMethod === 'Pay Later' || payload.paymentBreakdown?.some(payment => payment.channel === 'Pay Later');
    if (vdMart || payLater) {
      return await thunkAPI.dispatch(createOrder({ payload: { ...payload, phoneNo: phone || payload.phoneNo }, token, cartItems, updateStock: !vdMart })).unwrap();
    }
    const mkOrderId = payload?.MK_order_id ?? generateMKOrderId();

    const normalizedPayload = normalizeOrderDiscountPayload(withVdMartBuyer(payload, thunkAPI.getState()));

    const localOrder = {
      _localId: `OFF-${mkOrderId}`,
      queuedAt: new Date().toISOString(),
      payload: {
        ...normalizedPayload,
        MK_order_id: mkOrderId,
      },
      cartItems: cartItems || [],
      __token: token || null,
      phone: phone || null,
      customerMeta: customerMeta || null,
    };

    enqueueOrder(localOrder);
    return localOrder;
  }
);
// -----------------------------
// Create order on server (yours)
// -----------------------------
export const createOrder = createAsyncThunk(
  "orders/create",
  async ({ payload, token, cartItems, updateStock = true, useBuyerContext = true }, thunkAPI) => {
    try {
      const normalizedPayload = normalizeOrderDiscountPayload(useBuyerContext ? withVdMartBuyer(payload, thunkAPI.getState()) : payload);

      const response = await fetch(
        `${API_BASE_URL}/orders/pos`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(normalizedPayload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Order creation failed");
      }

      const stockErrors = [];

      if (updateStock && data.catalogSource !== 'ProductsVdMart' && data._id) {
        // The server takes this bill's quantities off the live stock (never a fixed number
        // from this screen, which may be out of date after a dispatch is received).
        try {
          await thunkAPI.dispatch(applyOrderStock({ orderId: data._id, token })).unwrap();
        } catch (err) {
          stockErrors.push({ orderId: data._id, error: err?.message || String(err) });
          console.warn("⚠️ Stock update failed for order:", data._id, err?.message || err);
        }
      } else if (data.catalogSource === 'ProductsVdMart') {
      // VD Mart stock is saved with the order on the server; this only refreshes the screen.
      for (const item of cartItems || []) {
        const newStock = item.stock;

        if (newStock >= 0) {
          try {
            await thunkAPI
              .dispatch(
                updateProductStockOnly({
                  productID: item.id,
                  brandID: item.brandId,
                  financialID: item.financialId,
                  location: item.location,
                  newQuantity: newStock,
                  token,
                })
              )
              .unwrap();
          } catch (err) {
            stockErrors.push({
              productID: item.id,
              brandID: item.brandId,
              financialID: item.financialId,
              error: err?.message || String(err),
            });

            console.warn(
              "⚠️ Stock update failed for product:",
              item.id,
              err?.message || err
            );
          }
        }
      }
      }

      return {
        ...data,
        stockErrors,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.message || "Order creation failed");
    }
  }
);

// // ------------------------------------------------------
// // Publish queued orders, one-by-one, using createOrder()
// // ------------------------------------------------------
// export const publishQueuedOrdersSequential = createAsyncThunk(
//   'orders/publishQueuedSequential',
//   async ({ token }, thunkAPI) => {
//     if (typeof navigator !== 'undefined' && !navigator.onLine) {
//       return thunkAPI.rejectWithValue('Offline: cannot publish now');
//     }

//     const queue = peekOrdersQueue();
//     if (!queue.length) return { published: 0, failed: 0, results: [] };

//     const results = [];
//     const remaining = [];
//     let published = 0;

//     for (const item of queue) {
//       const { payload, cartItems, _localId, __token } = item;
//       const tok = __token || token || thunkAPI.getState().posUser?.userInfo?.token;

//       try {
//         const res = await thunkAPI
//           .dispatch(createOrder({ payload, token: tok, cartItems }))
//           .unwrap();

//         results.push({ localId: _localId, ok: true, serverId: res?._id });
//         published += 1;
//         // success → do not push to remaining
//       } catch (e) {
//         results.push({ localId: _localId, ok: false, error: e?.message || String(e) });
//         remaining.push(item); // keep failed ones
//       }
//     }

//     setOrdersQueue(remaining);
//     return { published, failed: remaining.length, results };
//   }
// );
// Publish queued orders sequentially, verifying/creating customer first
export const publishQueuedOrdersSequential = createAsyncThunk(
  'orders/publishQueuedSequential',
  async ({ token }, thunkAPI) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return thunkAPI.rejectWithValue('Offline: cannot publish now');
    }

    const state = thunkAPI.getState();
    const fallbackToken = state.posUser?.userInfo?.token;
    // console.log("sssss"+token)
    const queue = peekOrdersQueue();
    if (!queue.length) return { published: 0, failed: 0, results: [] };

    const remaining = [];
    const results = [];
    let published = 0;

    for (const item of queue) {
      const tok = item.__token || token || fallbackToken;
      // console.log(tok)
      let payload = { ...item.payload }; // don’t mutate the queued snapshot

      try {
        // 1) Ensure customer is valid on server (by phone if we have it)
        const phone = item.phone;
        if (phone) {
          let cust = null;
          try {
            cust = await thunkAPI.dispatch(fetchCustomerByPhone({ phone, token: tok })).unwrap();
          } catch {
            cust = null;
          }
          if (!cust?._id) {
            const name = item.customerMeta?.name || 'NA';
            // build address in whatever format your API expects
            const addr =
              item.customerMeta?.address || item.customerMeta?.city || item.customerMeta?.postalCode
                ? { street: item.customerMeta?.address || 'NA',
                    city: item.customerMeta?.city || 'NA',
                    postalCode: item.customerMeta?.postalCode || '000000' }
                : 'NA';
            try {
              cust = await thunkAPI
                .dispatch(createCustomer({ name, phone, address: addr, token: tok }))
                .unwrap();
            } catch (e) {
              results.push({ localId: item._localId, ok: false, error: 'Customer create failed: ' + (e?.message || e) });
              remaining.push(item);
              continue; // next queue item
            }
          }
          payload.user = cust._id;
        }

        // 2) Create order on server (sequential via await)
        const res = await thunkAPI
          .dispatch(createOrder({ payload, token: tok, cartItems: item.cartItems, useBuyerContext: false }))
          .unwrap();

        results.push({ localId: item._localId, ok: true, serverId: res?._id });
        published += 1;
        // success → not pushed to remaining
      } catch (e) {
        results.push({ localId: item._localId, ok: false, error: e?.message || String(e) });
        remaining.push(item); // keep to retry later
      }
    }

    setOrdersQueue(remaining);
    return { published, failed: remaining.length, results };
  }
);
// -----------------------------
// Other existing thunks (kept as-is)
// -----------------------------
export const fetchLatestOrders = createAsyncThunk(
  'orders/fetchLatest',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().posUser?.userInfo?.token;
    const response = await fetch(`${API_BASE_URL}/orders/pos`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to fetch orders');
    return data;
  }
);

export const fetchPackingOrders = createAsyncThunk(
  'orders/fetchPacking',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().posUser?.userInfo?.token;
    const response = await fetch(`${API_BASE_URL}/orders/pos/orders/packing`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to fetch packing orders');
    return data;
  }
);

export const fetchDispatchOrders = createAsyncThunk(
  'orders/fetchDispatch',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().posUser?.userInfo?.token;
    const response = await fetch(`${API_BASE_URL}/orders/pos/orders/dispatch`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to fetch dispatch orders');
    return data;
  }
);

export const fetchDeliveryOrders = createAsyncThunk(
  'orders/fetchDelivery',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().posUser?.userInfo?.token;
    const response = await fetch(`${API_BASE_URL}/orders/pos/orders/delivery`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to fetch delivery orders');
    return data;
  }
);

export const fetchAllOrdersWithTimers = createAsyncThunk(
  'orders/fetchAllTimers',
  async (_, thunkAPI) => {
    const token = thunkAPI.getState().posUser?.userInfo?.token;
    const response = await fetch(`${API_BASE_URL}/orders/pos/orders/all`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to fetch all timed orders');
    return data;
  }
);

export const markOrderAsPacked = createAsyncThunk(
  'orders/markPacked',
  async ({ id, token }, thunkAPI) => {
    const res = await fetch(`${API_BASE_URL}/orders/pos/${id}/mark-packed`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to mark packed');
    return data;
  }
);

export const markOrderAsDispatched = createAsyncThunk(
  'orders/markDispatched',
  async ({ id, token }, thunkAPI) => {
    const res = await fetch(`${API_BASE_URL}/orders/pos/${id}/mark-dispatched`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to mark dispatched');
    return data;
  }
);

export const markOrderAsDelivered = createAsyncThunk(
  'orders/markAsDelivered',
  async (orderId, { rejectWithValue, getState }) => {
    try {
      const token = getState().posUser?.userInfo?.token;
      const response = await fetch(`${API_BASE_URL}/orders/pos/${orderId}/mark-delivered`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to mark as delivered');
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markOrderAsPaid = createAsyncThunk(
  'orders/markAsPaid',
  async (orderId, { rejectWithValue, getState }) => {
    try {
      const token = getState().posUser?.userInfo?.token;
      const response = await fetch(`${API_BASE_URL}/orders/pos/${orderId}/mark-paid`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to mark as paid');
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const fetchPOSOrders = createAsyncThunk(
  'orders/fetchPOSOrders',
  async ({
    mode = 'latest',
    phone = '',
    customerNumber = '',
    from = '',
    to = '',
    date = '',
    posUser = '',
    location = '',
  } = {}, thunkAPI) => {
    try {
      const token = thunkAPI.getState().posUser?.userInfo?.token || getStoredToken();

      const params = new URLSearchParams();
      params.set('mode', mode);

      if (phone) params.set('phone', phone);
      if (customerNumber) params.set('customerNumber', customerNumber);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (date) params.set('date', date);
      if (posUser) params.set('posUser', posUser);
      if (location) params.set('location', location);

      const url = `${API_BASE_URL}/orders/pos/orders/search?${params.toString()}`;
      const requestOptions = {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      };

      let response;
      try {
        response = await fetch(url, requestOptions);
      } catch (error) {
        if (error?.name === 'TypeError' && error?.message === 'Failed to fetch') {
          await wait(800);
          response = await fetch(url, requestOptions);
        } else {
          throw error;
        }
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to fetch POS orders');
      return data;
    } catch (error) {
      if (error?.name === 'TypeError' && error?.message === 'Failed to fetch') {
        return thunkAPI.rejectWithValue(
          navigator.onLine
            ? getNetworkFailureMessage('Finance data load')
            : 'No internet connection. Connect to the internet and try Refresh.'
        );
      }

      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch POS orders');
    }
  }
);

export const fetchPOSOrderDetails = createAsyncThunk(
  'orders/fetchPOSOrderDetails',
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().posUser?.userInfo?.token || getStoredToken();

      const response = await fetch(
        `${API_BASE_URL}/orders/pos/orders/details/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        }
      );

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        const preview = text.replace(/\s+/g, ' ').trim().slice(0, 80);
        throw new Error(
          `Order details API returned non-JSON response from ${API_BASE_URL}. ${preview}`
        );
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to fetch order details');
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch order details');
    }
  }
);

export const updatePOSOrder = createAsyncThunk(
  'orders/updatePOSOrder',
  async ({ id, orderItems, remarks = '', discountPercentage, discountAmount, totalPrice }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().posUser?.userInfo?.token || getStoredToken();
      const payload = { remarks };

      if (orderItems !== undefined) payload.orderItems = orderItems;
      if (discountPercentage !== undefined) payload.discountPercentage = discountPercentage;
      if (discountAmount !== undefined) payload.discountAmount = discountAmount;
      if (totalPrice !== undefined) payload.totalPrice = totalPrice;

      // Existing orders retain their saved buyer; the active checkout cart is unrelated.
      const normalizedPayload = normalizeOrderDiscountPayload(payload);

      const response = await fetch(`${API_BASE_URL}/orders/pos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(normalizedPayload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to update order');
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to update order');
    }
  }
);

export const deletePOSOrderItem = createAsyncThunk(
  'orders/deletePOSOrderItem',
  async ({ orderId, itemId, remarks = '' }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().posUser?.userInfo?.token || getStoredToken();

      const response = await fetch(
        `${API_BASE_URL}/orders/pos/${orderId}/items/${itemId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ remarks }),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to delete item');
      return { orderId, itemId, order: data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete item');
    }
  }
);

export const deletePOSOrder = createAsyncThunk(
  'orders/deletePOSOrder',
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().posUser?.userInfo?.token || getStoredToken();

      const response = await fetch(`${API_BASE_URL}/orders/pos/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to delete order');
      return { id, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete order');
    }
  }
);

export const rollbackPOSOrder = createAsyncThunk(
  'orders/rollbackPOSOrder',
  async ({ id, reason }, thunkAPI) => {
    const token = thunkAPI.getState().posUser?.userInfo?.token || getStoredToken();
    const response = await fetch(`${API_BASE_URL}/orders/pos/${id}/rollback`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) return thunkAPI.rejectWithValue(data.message || 'Order rollback failed');
    return data;
  }
);

// -----------------------------
// Slice
// -----------------------------
const orderSlice = createSlice({
  name: 'orders',
  initialState: {
    all: [],
    latest: null,
    recent: [],
    packing: [],
    dispatch: [],
    delivery: [],
    managerView: [],
    loading: false,
    error: '',
    // offline queue/publish ui
    queueCount: peekOrdersQueue().length,
    publishStatus: 'idle',
    publishMsg: '',
    lastPublishResults: [],
      posOrdersList: [],
  posOrdersListLoading: false,
  posOrdersListError: '',
    posOrderDetails: null,
  posOrderDetailsLoading: false,
  posOrderDetailsError: '',
  posOrderMutationLoading: false,
  posOrderMutationError: '',
  deliveryPosUsers: [],
  deliveryPosUsersLoading: false,
  deliveryPosUsersError: '',
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // queue locally
      .addCase(queueOrder.fulfilled, (state, action) => {
        state.recent = [{ ...action.payload, _status: 'QUEUED' }, ...state.recent].slice(0, 50);
        state.queueCount = peekOrdersQueue().length;
      })

      // create on server (existing)
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.latest = action.payload;
        if (!Array.isArray(state.all)) state.all = [];
        state.all.unshift(action.payload);
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // publish queued → sequential
      .addCase(publishQueuedOrdersSequential.pending, (state) => {
        state.publishStatus = 'loading';
        state.publishMsg = '';
        state.lastPublishResults = [];
      })
      .addCase(publishQueuedOrdersSequential.fulfilled, (state, action) => {
        state.publishStatus = 'succeeded';
        const { published, failed, results } = action.payload || {};
        state.publishMsg = `Published ${published} order(s).${failed ? ` ${failed} failed.` : ''}`;
        state.queueCount = peekOrdersQueue().length;
        state.lastPublishResults = results || [];
      })
      .addCase(publishQueuedOrdersSequential.rejected, (state, action) => {
        state.publishStatus = 'failed';
        state.publishMsg = action.payload || action.error?.message || 'Publish failed';
      })

      // existing lists…
      .addCase(fetchLatestOrders.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchLatestOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.recent = action.payload;
      })
      .addCase(fetchLatestOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchPackingOrders.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchPackingOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(fetchPackingOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchDispatchOrders.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchDispatchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.dispatch = action.payload;
      })
      .addCase(fetchDispatchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchDeliveryOrders.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchDeliveryOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.delivery = action.payload;
      })
      .addCase(fetchDeliveryOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllOrdersWithTimers.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchAllOrdersWithTimers.fulfilled, (state, action) => {
        state.loading = false;
        state.managerView = action.payload;
      })
      .addCase(fetchAllOrdersWithTimers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(markOrderAsPacked.fulfilled, (state, action) => {
        state.updatedOrder = action.payload;
      })
      .addCase(markOrderAsDispatched.fulfilled, (state, action) => {
        state.updatedOrder = action.payload;
      })
      .addCase(markOrderAsDelivered.fulfilled, (state, action) => {
        state.updatedOrder = action.payload;
      })
      .addCase(markOrderAsPaid.pending, (state) => {
        state.paidStatus = { loading: true, success: false, error: null };
      })
      .addCase(markOrderAsPaid.fulfilled, (state) => {
        state.paidStatus = { loading: false, success: true, error: null };
      })
      .addCase(markOrderAsPaid.rejected, (state, action) => {
        state.paidStatus = { loading: false, success: false, error: action.payload };
      })
            .addCase(fetchPOSOrders.pending, (state, action) => {
        state.posOrdersListLoading = true;
        state.posOrdersListError = '';
        state.posOrdersListRequestId = action.meta.requestId;
        state.posOrdersListQuery = action.meta.arg || { mode: 'today' };
      })
      .addCase(fetchPOSOrders.fulfilled, (state, action) => {
        if (state.posOrdersListRequestId !== action.meta.requestId) return;
        state.posOrdersListLoading = false;
        state.posOrdersList = action.payload || [];
      })
      .addCase(fetchPOSOrders.rejected, (state, action) => {
        if (state.posOrdersListRequestId !== action.meta.requestId) return;
        state.posOrdersListLoading = false;
        state.posOrdersListError = action.payload || 'Failed to fetch POS orders';
      })

      .addCase(fetchPOSOrderDetails.pending, (state) => {
        state.posOrderDetailsLoading = true;
        state.posOrderDetailsError = '';
      })
      .addCase(fetchPOSOrderDetails.fulfilled, (state, action) => {
        state.posOrderDetailsLoading = false;
        state.posOrderDetails = action.payload;
        const fetchedOrder = action.payload;
        const fetchedId = fetchedOrder?._id || fetchedOrder?.id;

        if (fetchedId) {
          state.posOrdersList = (state.posOrdersList || []).map((order) =>
            order._id === fetchedId || order.id === fetchedId
              ? { ...order, ...fetchedOrder }
              : order
          );
        }
      })
      .addCase(fetchPOSOrderDetails.rejected, (state, action) => {
        state.posOrderDetailsLoading = false;
        state.posOrderDetailsError = action.payload || 'Failed to fetch order details';
      })
      .addCase(updatePOSOrder.pending, (state) => {
        state.posOrderMutationLoading = true;
        state.posOrderMutationError = '';
      })
      .addCase(updatePOSOrder.fulfilled, (state, action) => {
        state.posOrderMutationLoading = false;
        const updatedOrder = action.payload?.order || action.payload;
        state.posOrderDetails = updatedOrder;
        const updatedId = updatedOrder?._id || updatedOrder?.id;

        if (updatedId) {
          state.posOrdersList = (state.posOrdersList || []).map((order) =>
            order._id === updatedId || order.id === updatedId
              ? { ...order, ...updatedOrder }
              : order
          );
        }
      })
      .addCase(updatePOSOrder.rejected, (state, action) => {
        state.posOrderMutationLoading = false;
        state.posOrderMutationError = action.payload || 'Failed to update order';
      })
      .addCase(deletePOSOrderItem.pending, (state) => {
        state.posOrderMutationLoading = true;
        state.posOrderMutationError = '';
      })
      .addCase(deletePOSOrderItem.fulfilled, (state, action) => {
        state.posOrderMutationLoading = false;
        state.posOrderDetails = action.payload?.order?.order || action.payload?.order;
      })
      .addCase(deletePOSOrderItem.rejected, (state, action) => {
        state.posOrderMutationLoading = false;
        state.posOrderMutationError = action.payload || 'Failed to delete item';
      })
      .addCase(deletePOSOrder.pending, (state) => {
        state.posOrderMutationLoading = true;
        state.posOrderMutationError = '';
      })
      .addCase(deletePOSOrder.fulfilled, (state, action) => {
        state.posOrderMutationLoading = false;
        state.posOrdersList = (state.posOrdersList || []).filter(
          (order) => order._id !== action.payload.id
        );
        if (state.posOrderDetails?._id === action.payload.id) {
          state.posOrderDetails = null;
        }
      })
      .addCase(deletePOSOrder.rejected, (state, action) => {
        state.posOrderMutationLoading = false;
        state.posOrderMutationError = action.payload || 'Failed to delete order';
      })
      .addCase(fetchDeliveryPosUsers.pending, (state) => {
        state.deliveryPosUsersLoading = true;
        state.deliveryPosUsersError = '';
      })
      .addCase(fetchDeliveryPosUsers.fulfilled, (state, action) => {
        state.deliveryPosUsersLoading = false;
        state.deliveryPosUsers = action.payload || [];
      })
      .addCase(fetchDeliveryPosUsers.rejected, (state, action) => {
        state.deliveryPosUsersLoading = false;
        state.deliveryPosUsersError =
          action.payload || 'Failed to fetch delivery users';
      });
  },
});

export default orderSlice.reducer;
